import { useState, useEffect, useRef } from 'react';

interface DocumentWithFullscreen extends Document {
  webkitFullscreenElement?: Element;
  mozFullscreenElement?: Element;
  msFullscreenElement?: Element;
}

interface UseFullScreenOptions {
  backgroundColor?: string;
}

/**
 * Checks if the document is currently in fullscreen mode
 */
export const isInFullScreenMode = (): boolean => {
  const doc = document as DocumentWithFullscreen;
  return !!(
    doc.fullscreenElement ||
    doc.webkitFullscreenElement ||
    doc.mozFullscreenElement ||
    doc.msFullscreenElement
  );
};

/**
 * Hook that tracks fullscreen state and provides methods to toggle fullscreen
 * @param elementRef Optional ref to the element to make fullscreen (defaults to document.documentElement)
 * @returns Object containing fullscreen state and control methods
 */
export const useFullScreen = (
  elementRef?: React.RefObject<HTMLElement>,
  options?: UseFullScreenOptions
) => {
  const [isFullScreen, setIsFullScreen] = useState<boolean>(
    isInFullScreenMode()
  );
  const originalBgColor = useRef<string | null>(null);
  const { backgroundColor = 'white' } = options || {};

  // Update state when fullscreen changes
  useEffect(() => {
    const handleFullscreenChange = () => {
      const newFullScreenState = isInFullScreenMode();
      setIsFullScreen(newFullScreenState);

      if (
        !newFullScreenState &&
        elementRef?.current &&
        originalBgColor.current !== null
      ) {
        elementRef.current.style.backgroundColor = originalBgColor.current;
      }
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    document.addEventListener('webkitfullscreenchange', handleFullscreenChange);
    document.addEventListener('mozfullscreenchange', handleFullscreenChange);
    document.addEventListener('MSFullscreenChange', handleFullscreenChange);

    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
      document.removeEventListener(
        'webkitfullscreenchange',
        handleFullscreenChange
      );
      document.removeEventListener(
        'mozfullscreenchange',
        handleFullscreenChange
      );
      document.removeEventListener(
        'MSFullscreenChange',
        handleFullscreenChange
      );
    };
  }, [elementRef]);

  // Function to enter fullscreen
  const enterFullScreen = async () => {
    try {
      const element = (elementRef?.current || document.documentElement) as any;

      if (elementRef?.current) {
        originalBgColor.current =
          window.getComputedStyle(element).backgroundColor;
        elementRef.current.style.backgroundColor = backgroundColor;
      }

      if (element.requestFullscreen) {
        await element.requestFullscreen();
      } else if (element.webkitRequestFullscreen) {
        await element.webkitRequestFullscreen();
      } else if (element.mozRequestFullScreen) {
        await element.mozRequestFullScreen();
      } else if (element.msRequestFullscreen) {
        await element.msRequestFullscreen();
      }
    } catch (error) {
      console.error('Failed to enter fullscreen mode:', error);
    }
  };

  // Function to exit fullscreen
  const exitFullScreen = async () => {
    try {
      const doc = document as any;

      if (doc.exitFullscreen) {
        await doc.exitFullscreen();
      } else if (doc.webkitExitFullscreen) {
        await doc.webkitExitFullscreen();
      } else if (doc.mozCancelFullScreen) {
        await doc.mozCancelFullScreen();
      } else if (doc.msExitFullscreen) {
        await doc.msExitFullscreen();
      }
    } catch (error) {
      console.error('Failed to exit fullscreen mode:', error);
    }
  };

  // Function to toggle fullscreen
  const toggleFullScreen = async () => {
    if (isFullScreen) {
      await exitFullScreen();
    } else {
      await enterFullScreen();
    }
  };

  return {
    isFullScreen,
    enterFullScreen,
    exitFullScreen,
    toggleFullScreen,
  };
};
